/**
 * ConfigurationRibbon.js - Composant ruban flottant pour configuration des modules
 * Ruban centré verticalement qui s'étire pour afficher l'interface de configuration
 */
import "./configuration-ribbon.css";
import { Module } from "../Module/Module.js";
import { createElement } from "../../utils/dom.js";

export class ConfigurationRibbon {
  constructor(parentElement) {
    this.parentElement = parentElement || document.body;

    // État du ruban
    this.isOpen = false;
    this.currentModule = null;
    this.currentModuleTitle = null;
    this.moduleInstance = null;

    // Le ruban crée son propre container
    this.container = null;

    // Initialiser le composant
    this.render();

    // Écouter les événements globaux
    this.setupGlobalListeners();
  }

  /**
   * Créer le container du ruban
   */
  createContainer() {
    this.container = createElement("div", {
      className: "riban-config",
      id: "ConfigurationRibbon",
    });

    // Injecter dans le parent (généralement body)
    this.parentElement.appendChild(this.container);

    return this.container;
  }

  /**
   * Ouvrir le ruban avec un module spécifique
   */
  openModule(moduleTitle) {
    if (this.isOpen && this.currentModuleTitle === moduleTitle) {
      // Déjà ouvert sur ce module
      return;
    }

    // Fermer le module précédent si nécessaire
    if (this.moduleInstance) {
      this.moduleInstance.destroy();
      this.moduleInstance = null;
    }

    // Mettre à jour l'état
    this.isOpen = true;
    this.currentModuleTitle = moduleTitle;

    // Afficher l'interface
    this.renderOpenState();
  }

  /**
   * Fermer le ruban
   */
  close() {
    if (!this.isOpen) return;

    // Détruire le module actuel
    if (this.moduleInstance) {
      this.moduleInstance.destroy();
      this.moduleInstance = null;
    }

    // Mettre à jour l'état
    this.isOpen = false;
    this.currentModuleTitle = null;

    // Afficher l'état fermé
    this.renderClosedState();
  }

  /**
   * Rendu de l'état fermé (ruban vertical)
   */
  renderClosedState() {
    this.container.className = "riban-config riban-closed";
    this.container.innerHTML = `
      <div class="riban-content-closed">
        <div class="riban-text-vertical">
          <span>C</span>
          <span>O</span>
          <span>N</span>
          <span>F</span>
          <span>I</span>
          <span>G</span>
          <span>U</span>
          <span>R</span>
          <span>A</span>
          <span>T</span>
          <span>I</span>
          <span>O</span>
          <span>N</span>
        </div>
      </div>
    `;
  }

  /**
   * Rendu de l'état ouvert (interface module)
   */
  renderOpenState() {
    this.container.className = "riban-config riban-open";

    // Structure de l'interface ouverte
    this.container.innerHTML = `
      <div class="riban-content-open">
        <div class="riban-header">
          <h3 class="riban-title">📝 Édition Configuration Module</h3>
        </div>
        <div class="riban-module-container" id="ribanModuleContainer">
          <!-- Le module sera injecté ici -->
        </div>
      </div>
    `;

    // Plus de bouton de fermeture - on utilise Échap ou clic dehors

    // Créer l'instance du module
    const moduleContainer = this.container.querySelector("#ribanModuleContainer");
    if (moduleContainer) {
      this.moduleInstance = new Module(this.currentModuleTitle, moduleContainer);
    }
  }

  /**
   * Rendu initial du ruban
   */
  render() {
    // Créer le container si nécessaire
    if (!this.container) {
      this.createContainer();
    }

    // État initial : fermé
    this.renderClosedState();
  }

  /**
   * Écouter les événements globaux
   */
  setupGlobalListeners() {
    // 🎯 PRINCIPAL : Écouter les événements d'édition de modules
    window.addEventListener("moduleEditRequested", (event) => {
      const { moduleTitle, moduleName, tiroirTitle } = event.detail;
      this.openModule(moduleTitle);
    });

    // 🎯 NOUVEAU : Écouter les demandes de fermeture depuis les modules
    window.addEventListener("closeRibanRequested", (event) => {
      this.close();
    });

    // Fermer au refresh/rechargement
    window.addEventListener("beforeunload", () => {
      this.close();
    });

    // Échap pour fermer
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Optionnel : Clic en dehors pour fermer
    document.addEventListener("click", (event) => {
      if (this.isOpen && !this.container.contains(event.target)) {
        // Ne fermer que si on clique vraiment en dehors
        const isEditButton = event.target.closest(".tiroir-edit-btn");
        if (!isEditButton) {
          // Optionnel: décommenter pour fermer en cliquant dehors
          // this.close();
        }
      }
    });
  }

  /**
   * Obtenir l'état actuel du ruban
   */
  getState() {
    return {
      isOpen: this.isOpen,
      currentModule: this.currentModuleTitle,
      hasModule: !!this.moduleInstance,
    };
  }

  /**
   * Vérifier si un module spécifique est ouvert
   */
  isModuleOpen(moduleTitle) {
    return this.isOpen && this.currentModuleTitle === moduleTitle;
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // Détruire le module actuel
    if (this.moduleInstance) {
      this.moduleInstance.destroy();
      this.moduleInstance = null;
    }

    // Supprimer les événements spécifiques
    window.removeEventListener("moduleEditRequested", this.setupGlobalListeners);
    window.removeEventListener("beforeunload", this.close);
    document.removeEventListener("keydown", this.setupGlobalListeners);
    document.removeEventListener("click", this.setupGlobalListeners);

    // Supprimer du DOM
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    // Nettoyer les références
    this.container = null;
    this.parentElement = null;
    this.moduleInstance = null;
  }
}
